import type { TFunction } from 'i18next';

import Yup from '@tmh-website/services/validation.service';
import { SelectOption } from '@tmh-website/ui/FormElements';

type CustomerTypeOptionValue =
  | 'ev_driver'
  | 'private_customer'
  | 'company/organization'
  | 'electrician_private_person'
  | 'electrician_company/organization';

type QuestionTypeOptionValue =
  | 'Charging Events / Load Management'
  | 'Billing system'
  | 'CP Dashboard'
  | 'RFIDs'
  | 'Hardware defect / Error message'
  | 'Replacement'
  | 'Something else';

type RelevantAreaTypeOptionValue = 'home' | 'activities' | 'statistics' | 'user_access' | 'settings' | 'profile';

export interface CustomerTypeOption extends SelectOption {
  value: CustomerTypeOptionValue;
}

export interface QuestionTypeOption extends SelectOption {
  value: QuestionTypeOptionValue;
}

export interface RelevantAreaTypeOption extends SelectOption {
  value: RelevantAreaTypeOptionValue;
}

export type BusinessUnitSolutionsWeb2CaseFormValues = {
  first_name: string;
  last_name: string;
  company: string;
  postal_code: string;
  street: string;
  city: string;
  phone: string;
  email: string;
  customer_type: CustomerTypeOption['value'] | undefined;
  question: QuestionTypeOption['value'] | undefined;
  message?: string;
  chargepilot_site?: string;
  manufacturer?: string;
  serial_number?: string;
  old_serial_number?: string;
  new_serial_number?: string;
  relevant_area?: RelevantAreaTypeOption['value'];
  is_station_online?: string;
  'g-recaptcha-response': string;
};

export const validationSchema = (t: TFunction<'translation', undefined>) =>
  new Yup.ObjectSchema({
    first_name: Yup.string().required(t('validation:mixed.required')),
    last_name: Yup.string().required(t('validation:mixed.required')),
    postal_code: Yup.string().required(t('validation:mixed.required')),
    street: Yup.string().required(t('validation:mixed.required')),
    city: Yup.string().required(t('validation:mixed.required')),
    phone: Yup.string()
      .matches(/^(\+?[0-9]+)$/)
      .required(t('validation:mixed.required')),
    email: Yup.string().email(t('validation:string.email')).required(t('validation:mixed.required')),
    customer_type: Yup.mixed<CustomerTypeOption['value']>().required(t('validation:mixed.required')),
    question: Yup.mixed<QuestionTypeOption['value']>().required(t('validation:mixed.required')),
    message: Yup.string().when('question', {
      is: (value: QuestionTypeOption['value']) =>
        value === 'Hardware defect / Error message' || value === 'Replacement',
      then: Yup.string(),
      otherwise: Yup.string().required(t('validation:mixed.required')),
    }),
    chargepilot_site: Yup.string().when('question', {
      is: (value: QuestionTypeOption['value']) =>
        value === 'Charging Events / Load Management' ||
        value === 'CP Dashboard' ||
        value === 'RFIDs' ||
        value === 'Replacement',
      then: Yup.string().required(t('validation:mixed.required')),
      otherwise: Yup.string(),
    }),
    relevant_area: Yup.mixed<RelevantAreaTypeOption['value']>(),
    'g-recaptcha-response': Yup.string().required(t('validation:mixed.required')),
    old_serial_number: Yup.string().when('question', {
      is: 'replacement',
      then: Yup.string().required(t('validation:mixed.required')),
      otherwise: Yup.string(),
    }),
    new_serial_number: Yup.string().when('question', {
      is: 'replacement',
      then: Yup.string().required(t('validation:mixed.required')),
      otherwise: Yup.string(),
    }),
    is_station_online: Yup.string().when('question', {
      is: 'replacement',
      then: Yup.string().required(t('validation:mixed.required')),
      otherwise: Yup.string(),
    }),
  });
